import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowUp, Square } from 'lucide-react';

/** Message box for Master Kala: Enter sends, Shift+Enter adds a line, Stop while an answer streams. */
export function Composer({
  value,
  onChange,
  onSend,
  onStop,
  sending,
  capRemaining,
  limitNote,
  creditsNote,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  sending: boolean;
  capRemaining: number | null;
  /** Shown beside the spent-cap line: what a bigger plan would give them. */
  limitNote?: ReactNode;
  /** The member's AI Credits line ("12 credits left · Get more"); nothing when credits are off. */
  creditsNote?: ReactNode;
  /** A prompt from the library landed here: put the caret at its end so editing can start. */
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!autoFocus || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [autoFocus]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [value]);
  const capBlocked = capRemaining !== null && capRemaining <= 0;

  return (
    <div className="border-t border-border px-5 pb-4 pt-3">
      <div className="flex items-end gap-2">
        <label htmlFor="kala-message" className="sr-only">
          Message Master Kala
        </label>
        <textarea
          id="kala-message"
          ref={ref}
          rows={1}
          value={value}
          maxLength={4000}
          disabled={capBlocked}
          placeholder={capBlocked ? "You've used today's messages" : 'Ask your success coach…'}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              onSend();
            }
          }}
          className="max-h-[180px] min-h-11 flex-1 resize-none border border-[rgba(16,32,26,0.22)] bg-transparent px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-sage-deep focus:border-verde focus:outline-none disabled:opacity-50"
        />
        {sending ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop the answer"
            className="flex h-11 w-11 shrink-0 items-center justify-center border border-[rgba(16,32,26,0.22)] text-ink transition-colors hover:border-verde focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde"
          >
            <Square className="h-4 w-4" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={onSend}
            disabled={!value.trim() || capBlocked}
            aria-label="Send"
            className="flex h-11 w-11 shrink-0 items-center justify-center bg-gold text-ink transition-colors hover:bg-[#c29d4c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-verde disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowUp className="h-5 w-5" aria-hidden />
          </button>
        )}
      </div>
      {creditsNote && <p className="eyebrow mt-2">{creditsNote}</p>}
      {capRemaining !== null && (
        <p className="eyebrow mt-2">
          {capRemaining > 0
            ? `${capRemaining} ${capRemaining === 1 ? 'message' : 'messages'} left today`
            : 'Daily limit reached. It resets tomorrow.'}
          {capBlocked && limitNote}
        </p>
      )}
    </div>
  );
}
